import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import * as storage from "utils/local-storage";
import {
  IS_INTEGRATED_APP,
  TOKEN,
  USER_ID,
  REFRESH_TOKEN,
  SPEED_ACCOUNT_ID,
  SPEED_LIGHTNING_ADDRESS,
  SPEED_LANG,
  SPEED_BAL_BTC,
  SPEED_BAL_USDT,
} from "utils/constants";

export interface SpeedIntegrationData {
  // Speed Wallet Mini App account id (passed via `acct` URL param).
  accountId?: string;
  // Lightning address of the user (passed via `p_add` URL param).
  // Used as the default withdrawal destination.
  lightningAddress?: string;
  // UI language preference (passed via `lang`).
  lang?: string;
  // Speed-wallet balances at page load (in the respective native units).
  balanceBtc?: number;
  balanceUsdt?: number;
}

export interface IntegratedAppSlice {
  isIntegratedApp: boolean;
  state: IntegratedAppState;
  userId?: string;
  token?: string;
  refreshToken?: string;
  speed?: SpeedIntegrationData;
}

export enum IntegratedAppState {
  UNDEFINED,
  NOT_INTEGRATED,
  INTEGRATED_ANONYMOUS,
  INTEGRATED_SESSION_INITIALIZING,
  INTEGRATED_SESSION_INITIALIZED,
}

const initialState: IntegratedAppSlice = {
  isIntegratedApp: false,
  state: IntegratedAppState.UNDEFINED,
};

const slice = createSlice({
  name: "integratedApp",
  initialState,
  reducers: {
    setIntegratedState: (state, action) => {
      storage.session.setItem(IS_INTEGRATED_APP, "true");

      state.state = action.payload.state;
      state.isIntegratedApp = action.payload.isIntegratedApp;
      state.userId = action.payload.userId;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;

      if (state.userId) {
        storage.session.setItem(USER_ID, state.userId);
      }
      if (state.token) {
        storage.session.setItem(TOKEN, state.token);
      }
      if (state.refreshToken) {
        storage.session.setItem(REFRESH_TOKEN, state.refreshToken);
      }

      return state;
    },
    setSpeedIntegrationData: (state, action: PayloadAction<SpeedIntegrationData>) => {
      state.speed = { ...(state.speed || {}), ...action.payload };

      if (action.payload.accountId !== undefined) {
        storage.session.setItem(SPEED_ACCOUNT_ID, action.payload.accountId);
      }
      if (action.payload.lightningAddress !== undefined) {
        storage.session.setItem(SPEED_LIGHTNING_ADDRESS, action.payload.lightningAddress);
      }
      if (action.payload.lang !== undefined) {
        storage.session.setItem(SPEED_LANG, action.payload.lang);
      }
      if (action.payload.balanceBtc !== undefined) {
        storage.session.setItem(SPEED_BAL_BTC, String(action.payload.balanceBtc));
      }
      if (action.payload.balanceUsdt !== undefined) {
        storage.session.setItem(SPEED_BAL_USDT, String(action.payload.balanceUsdt));
      }

      return state;
    },
    removeIntegratedState: (state) => {
      storage.session.removeItem(IS_INTEGRATED_APP);
      storage.session.removeItem(USER_ID);
      storage.session.removeItem(TOKEN);
      storage.session.removeItem(REFRESH_TOKEN);
      storage.session.removeItem(SPEED_ACCOUNT_ID);
      storage.session.removeItem(SPEED_LIGHTNING_ADDRESS);
      storage.session.removeItem(SPEED_LANG);
      storage.session.removeItem(SPEED_BAL_BTC);
      storage.session.removeItem(SPEED_BAL_USDT);

      state.state = IntegratedAppState.NOT_INTEGRATED;
      state.isIntegratedApp = false;
      state.userId = "";
      state.token = "";
      state.refreshToken = "";
      state.speed = undefined;

      return state;
    }
  },
});

export const {
  setIntegratedState,
  setSpeedIntegrationData,
  removeIntegratedState,
} = slice.actions;

export const getIntegratedAppState = (state: RootState): IntegratedAppSlice => state.integratedApp;
export const getSpeedIntegrationData = (state: RootState): SpeedIntegrationData | undefined =>
  state.integratedApp.speed;

export default slice.reducer;
